/**
 * The arithmetic behind a plan's limits.
 *
 * Separate from the editor because these are the rules, not the widgets: what a
 * per-store allowance implies for the clinic, when a total is following that
 * rule and when somebody chose it, and how dollars become the micro-USD integers
 * the money column stores. Testable without a browser, and tested.
 */
import { PLAN_LIMIT_FIELDS } from '../../shared/plans.ts';

/** The editor's state: every limit as text, so "empty" can mean unlimited. */
export type LimitDraft = Record<string, string>;

export const draftFrom = (limits: Record<string, number>): LimitDraft =>
  Object.fromEntries(
    PLAN_LIMIT_FIELDS.map(({ key }) => [key, limits[key] === undefined ? '' : String(limits[key])]),
  );

/**
 * The clinic total a per-store allowance implies: per store × stores.
 *
 * This is the number almost every plan wants, and asking for it separately was
 * asking the same question twice - 「10 per store, 3 stores」 already says 30.
 * Either half missing means there is nothing to derive, and unlimited is the
 * honest answer rather than a guess.
 */
export function derivedTotal(perStore: string, stores: string): string {
  const a = perStore.trim();
  const b = stores.trim();
  if (a === '' || b === '') return '';
  const total = Number(a) * Number(b);
  return Number.isFinite(total) ? String(total) : '';
}

/**
 * Is this plan's clinic total simply per-store × stores.
 *
 * Decided by comparing the numbers rather than by remembering a checkbox: a
 * stored flag would have to be migrated, kept in step, and would still be wrong
 * for every plan written before it existed. If the total already equals what the
 * multiplication gives, the plan is following the rule and keeps following it
 * when the per-store number changes; if it does not, somebody chose it and it is
 * left alone.
 */
export function looksDerived(draft: LimitDraft, perStoreKey: string, totalKey: string): boolean {
  const total = (draft[totalKey] ?? '').trim();
  const derived = derivedTotal(draft[perStoreKey] ?? '', draft.stores ?? '');
  /*
   * Both blank counts as linked, which is what makes a NEW plan behave: the
   * editor opens empty and the first per-store number typed starts filling the
   * total in. An existing plan with a blank total and a per-store number set is
   * deliberately uncapped - derived is a figure, blank is not, so they differ
   * and the plan stays uncapped.
   */
  return total === derived;
}

/**
 * The draft as the API's partial map.
 *
 * Empty means unlimited and is sent as an explicit null - the API deletes the
 * key rather than leaving whatever was there. A blank box that quietly kept the
 * old number would be the worst of the three possible behaviours.
 */
export function limitsPayload(draft: LimitDraft): Record<string, number | null> {
  const payload: Record<string, number | null> = {};
  for (const { key } of PLAN_LIMIT_FIELDS) {
    const raw = (draft[key] ?? '').trim();
    payload[key] = raw === '' ? null : Number(raw);
  }
  return payload;
}

/** Rejects what the API would reject, with the field named. */
export function limitProblem(draft: LimitDraft): string | null {
  for (const { key, label } of PLAN_LIMIT_FIELDS) {
    const raw = (draft[key] ?? '').trim();
    if (raw === '') continue;
    const value = Number(raw);
    if (!Number.isInteger(value) || value < 0) return `${label}は0以上の整数で入力してください。`;
  }
  return null;
}

/** A draft value as a number, or undefined for "unlimited" - what limitText reads. */
export const numberOrUndefined = (raw: string | undefined) =>
  !raw || raw.trim() === '' ? undefined : Number(raw);

/** Micro-USD as dollars for the input, and back again on the way out. */
export const microToUsd = (raw: string) => (raw.trim() === '' ? '' : String(Number(raw) / 1_000_000));
export const usdToMicro = (raw: string) =>
  raw.trim() === '' ? '' : String(Math.round(Number(raw) * 1_000_000));
